import { type Context } from "@nuxt/types";
import { Ref, ref } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import logger, { logLevel } from "@/utils/logger";

import useRetailerProductLinksApi from "./-api";

interface RetailerProductLinksStoreContext {
  $api: ReturnType<typeof useRetailerProductLinksApi>;
  $store: Context["store"];
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function    useRetailerProductLinksStore
 *
 * @desc        A pinia store for interacting with the retailer product links API. The functions
 *              are wrappers around the useApi composable's request function. The
 *              returned functions CANNOT be destructured without losing reactivity.
 *
 * @summary     A composable that exposes the retailer product links store.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Usage of `useRetailerProductLinksStore`</caption>
 * ```ts
 *   const retailerProductLinksStore = useRetailerProductLinksStore();
 *
 *   // ❌ Do not destructure the returned functions
 *   const { fetchRetailerProductLinkById } = retailerProductLinksStore;
 *
 *   // ✅ Instead, use the returned functions directly
 *   await retailerProductLinksStore.fetchRetailerProductLinkById({ id, hlc});
 * ```
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useRetailerProductLinksStore = defineStore(
  "retailer-product-links",
  () => {
    // State
    const retailerProductLinks: Ref<RetailerProductLinksMap> = ref({});

    // Actions
    async function fetchRetailerProductLinkById(
      this: RetailerProductLinksStoreContext,
      params: GetRetailerProductLinkParams,
    ) {
      try {
        const response = await this.$api.get(params);

        retailerProductLinks.value = Object.assign({}, retailerProductLinks.value, {
          [response.data.retailerProductLink.retailerProductId]: response.data.retailerProductLink,
        });
      } catch (error) {
        logger(logLevel.ERROR, "Error fetching retailer product link from the consumer product service", { error });
      }
    }

    function $reset() {
      retailerProductLinks.value = {};
    }

    return {
      retailerProductLinks,
      fetchRetailerProductLinkById,
      $reset,
    };
  },
  {
    api: useRetailerProductLinksApi,
  },
);
